import React, { useState } from 'react'
import { HiX, HiClipboard, HiExternalLink } from 'react-icons/hi'
import toast from 'react-hot-toast'

const PlusCode = ({ plusCode, className = "", onGeneratePlusCode, latitude, longitude }) => {
  const [isOpen, setIsOpen] = useState(false)

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(plusCode)
      toast.success('Plus Code copied to clipboard!')
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
      toast.error('Failed to copy to clipboard')
    }
  }

  const handleWhatIsPlusCode = () => {
    window.open('https://maps.google.com/pluscodes', '_blank')
  }

  const handleMapPlusCode = () => {
    if (plusCode) {
      window.open(`https://plus.codes/${plusCode}`, '_blank')
    }
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  // Determine logo color based on plusCode presence
  const logoSrc = plusCode 
    ? '/PlusCodesLogo.png'  // Blue logo when plusCode exists
    : '/PlusCodesLogo.png'  // Same logo, but we'll use CSS to make it grey

  return (
    <>
      {/* PlusCode Logo Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`inline-flex items-center justify-center w-6 h-6 rounded transition-colors duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 ${className}`}
        title={plusCode ? `Plus Code: ${plusCode}` : 'No Plus Code available'}
      >
        <img
          src={logoSrc}
          alt="Plus Code"
          className={`w-5 h-5 transition-colors duration-200 ${
            plusCode 
              ? 'filter-none' // Blue (original color)
              : 'filter grayscale opacity-50' // Grey
          }`}
        />
      </button>

      {/* PlusCode Popup Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              {/* Header */}
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center">
                  <img
                    src="/PlusCodesLogo.png"
                    alt="Plus Code"
                    className="w-6 h-6 mr-2"
                  />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Plus Code
                  </h3>
                </div>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <HiX className="h-6 w-6" />
                </button>
              </div>

              {/* Content */}
              <div className="space-y-4">
                {/* Description */}
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Plus Codes are a way to represent any location on Earth with a simple code. 
                  They're useful for sharing precise locations without needing street addresses.
                  {!plusCode && (
                    <span className="block mt-2 text-amber-600 dark:text-amber-400">
                      <strong>Note:</strong> No Plus Code is currently saved for this location. 
                      You can update the coordinates to generate one.
                    </span>
                  )}
                </p>

                {/* Plus Code Display */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Plus Code
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={plusCode || 'Not saved - update coordinates to generate'}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono"
                    />
                    {plusCode && (
                      <button
                        onClick={handleCopyToClipboard}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        title="Copy to clipboard"
                      >
                        <HiClipboard className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col space-y-2">
                  <button
                    onClick={handleWhatIsPlusCode}
                    className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
                  >
                    <HiExternalLink className="h-4 w-4 mr-2" />
                    What is a Plus Code?
                  </button>
                  
                  {plusCode ? (
                    <button
                      onClick={handleMapPlusCode}
                      className="w-full flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
                    >
                      <HiExternalLink className="h-4 w-4 mr-2" />
                      Map This Plus Code
                    </button>
                  ) : (
                    latitude && longitude && onGeneratePlusCode && (
                      <button
                        onClick={() => onGeneratePlusCode(latitude, longitude)}
                        className="w-full flex items-center justify-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
                      >
                        <HiExternalLink className="h-4 w-4 mr-2" />
                        Generate Plus Code
                      </button>
                    )
                  )}
                </div>
              </div>

              {/* Close Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PlusCode
